import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Spinner } from './components/primitives';
import { AppLayout } from './layout/AppLayout';
import { LoginScreen } from './screens/auth/LoginScreen';
import { PendingScreen, NoAccessScreen } from './screens/auth/AccessScreens';
import { TrackerScreen } from './screens/tracker/TrackerScreen';
import { DashboardScreen } from './screens/dashboard/DashboardScreen';
import { ReportsScreen } from './screens/reports/ReportsScreen';
import { TimeOffScreen } from './screens/timeoff/TimeOffScreen';
import { ManageScreen } from './screens/manage/ManageScreen';

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex h-screen items-center justify-center bg-[#F6F7F9] dark:bg-slate-900">{children}</div>;
}

function ProtectedLayout() {
  const { status } = useAuth();
  if (status === 'loading')
    return (
      <FullScreen>
        <Spinner className="h-8 w-8" />
      </FullScreen>
    );
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (status === 'pending') return <Navigate to="/pending" replace />;
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function ManageGuard({ children }: { children: ReactNode }) {
  const { permissions } = useAuth();
  if (!permissions?.manage) return <Navigate to="/tracker" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/pending" element={<PendingScreen />} />
      <Route path="/no-access" element={<NoAccessScreen />} />
      <Route element={<ProtectedLayout />}>
        <Route index element={<Navigate to="/tracker" replace />} />
        <Route path="/tracker" element={<TrackerScreen />} />
        <Route path="/dashboard" element={<DashboardScreen />} />
        <Route path="/reports" element={<ReportsScreen />} />
        <Route path="/timeoff" element={<TimeOffScreen />} />
        <Route
          path="/manage"
          element={
            <ManageGuard>
              <ManageScreen />
            </ManageGuard>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
